import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams } from "react-router";
import { useEndpoints } from "../../api/ApiContext";
import { gaps } from "../../api/gaps";
import type { ConnectAccountStatus } from "../../api/types";
import { RequireMfa } from "../../auth/guards";
import { Banner, Button, ButtonLink, Card, ErrorState, PageHeader, QueryView, StatusPill } from "../../components/ui";
import { formatLocal } from "../../lib/datetime";
import { useIntentKey } from "../../lib/useIntentKey";
import { PayoutDestinationCard } from "./PayoutDestination";

// UX-DESIGN.md §3.0 publisher onboarding & payout eligibility.
//   GET  /v1/payouts/connect-account  — mirror of Stripe's account (webhook-fed; poll freely)
//   POST /v1/payouts/connect-account  — creates the account if needed, returns a fresh
//                                       single-use onboarding link. RequireRecentMFA.
// Stripe sends the publisher back to /payouts/connect-account/return or
// /refresh (publisher.go:409-410). KYC/KYB status is G-10. The payout
// destination (G-42, 0065) is PayoutDestination.tsx; it replaces the 0023
// site-scoped destination request, which answers 410 endpoint_replaced.
// Approving or canceling a change is the shared Pending approvals screen
// (G-09, org/Approvals.tsx).

export function PayoutSetupPage() {
  return (
    <div className="stack-lg">
      <PageHeader
        title="Payout setup"
        description="You can add sites and run traffic before this is done; payouts need it."
      />
      <RequireMfa>
        <div className="stack-lg">
          <ConnectCard />
          <PayoutDestinationCard />
        </div>
      </RequireMfa>
      <ComplianceCard />
    </div>
  );
}

function statusLine(s: ConnectAccountStatus): { tone: "success" | "warning" | "neutral"; label: string; detail: string } {
  if (!s.linked) return { tone: "neutral", label: "Not started", detail: "Set up a payout account with our payment provider, Stripe. You'll give them your identity and bank details directly." };
  if (s.payouts_enabled) return { tone: "success", label: "Payouts enabled", detail: "Your payout account is ready." };
  if (s.details_submitted)
    return { tone: "warning", label: "Under review by Stripe", detail: "You've submitted your details. Stripe is reviewing them, or needs something more — continue onboarding to check." };
  return { tone: "warning", label: "Onboarding incomplete", detail: "Continue onboarding with Stripe to finish setting up payouts." };
}

function ConnectCard() {
  const ep = useEndpoints();
  const intent = useIntentKey();
  const q = useQuery({ queryKey: ["connect-status"], queryFn: () => ep.publisher.getConnectAccountStatus() });
  const m = useMutation({
    // One key per "get a link" intent (G-21, 0057): a retry after a lost
    // response replays the same link instead of calling Stripe again.
    mutationFn: () => ep.publisher.createConnectOnboardingLink({ idempotencyKey: intent.keyFor("connect") }),
    onSuccess: (res) => {
      intent.reset();
      window.location.assign(res.onboarding_url);
    },
  });
  return (
    <Card title="Payout account">
      <QueryView query={q} loadingLabel="Loading payout account status">
        {(s) => {
          const line = statusLine(s);
          return (
            <div className="stack">
              <StatusPill tone={line.tone}>{line.label}</StatusPill>
              <p>{line.detail}</p>
              {s.status_updated_at ? (
                <p className="xs muted">Last update from Stripe: {formatLocal(s.status_updated_at)}. This updates when Stripe notifies us.</p>
              ) : null}
              {m.isError ? <ErrorState error={m.error} /> : null}
              {!s.payouts_enabled ? (
                <div>
                  <Button onClick={() => m.mutate()} loading={m.isPending || m.isSuccess}>
                    {s.linked ? "Continue onboarding with Stripe" : "Set up payouts with Stripe"}
                  </Button>
                </div>
              ) : null}
              <p className="xs muted">You'll confirm it's you first: this decides where your money is sent.</p>
            </div>
          );
        }}
      </QueryView>
    </Card>
  );
}

function ComplianceCard() {
  const q = useQuery({ queryKey: ["compliance"], queryFn: gaps.getComplianceStatus, retry: false });
  return (
    <Card title="Identity verification" subtitle="Required before payouts. Earnings accrue while it's pending and are held until it clears.">
      {q.isError ? <ErrorState error={q.error} /> : null}
    </Card>
  );
}

/** Stripe Connect return_url / refresh_url targets. */
export function ConnectReturnPage() {
  const { outcome } = useParams();
  const refresh = outcome === "refresh";
  const ep = useEndpoints();
  const intent = useIntentKey();
  const m = useMutation({
    // One key per "get a link" intent (G-21, 0057): a retry after a lost
    // response replays the same link instead of calling Stripe again.
    mutationFn: () => ep.publisher.createConnectOnboardingLink({ idempotencyKey: intent.keyFor("connect") }),
    onSuccess: (res) => {
      intent.reset();
      window.location.assign(res.onboarding_url);
    },
  });
  return (
    <div className="stack-lg">
      <PageHeader title={refresh ? "Onboarding link expired" : "Back from Stripe"} back={{ to: "/publisher/payout-setup", label: "Payout setup" }} />
      {refresh ? (
        <Banner tone="neutral" title="That link can only be used once and has expired">
          <div className="stack-sm">
            <span>Get a fresh link to continue where you left off.</span>
            {m.isError ? <ErrorState error={m.error} /> : null}
            <div>
              <Button onClick={() => m.mutate()} loading={m.isPending || m.isSuccess}>
                Continue onboarding
              </Button>
            </div>
          </div>
        </Banner>
      ) : (
        <Banner tone="info" title="Thanks — we're waiting for Stripe" live="polite">
          If you finished, Stripe will tell us within a few minutes and your payout status updates. If you left partway, you can continue any
          time.
        </Banner>
      )}
      <div>
        <ButtonLink to="/publisher/payout-setup" variant="primary">
          See payout status
        </ButtonLink>
      </div>
    </div>
  );
}
